/**
 * web/src/components/speaker/NudgeTimeline.tsx
 * D1's follow-up plan — a schedule that reads like a schedule. Lives on the
 * STEP PAGE of the email it chases (GateReview's sent view — 2026-07-14,
 * Danny's redesign): approve the send, then set up the chase right there.
 * One panel per sent email (NudgeSummary): FOLLOW-UP PLAN kicker + header,
 * the tracked thread line, then a vertical timeline of bumps with a
 * "TODAY · {date}" marker between past and future.
 *
 * Row states: sent (immutable history — the server 409s on edit; shows the
 * fire-time sentSummary of what it chased), the NEXT upcoming planned bump
 * (emphasized), later planned, overdue planned (amber), planned with a send
 * error (red "Send failed · retrying"), cancelled (with the human reason).
 * Cancelled rows collapse behind "Cancelled earlier (N) — {reason}".
 *
 * `chaseOver` (paired receipt step already done): neutral "chase closed"
 * pill and the add/stop footer disappears — adding bumps to a closed chase
 * is a footgun the server would auto-cancel anyway.
 *
 * Interactions (BINDING semantics):
 *   - every edit is a FULL-LIST replace (useUpdateNudgePlan) with sent entries
 *     riding along verbatim
 *   - reschedule commits optimistically with an undo toast that re-PATCHes
 *     the previous list
 *   - Send now AND Preview both open the review sheet — nothing sends blind
 *   - Add bump appends +5 business days after the last planned entry
 *   - Stop all confirms first, then cancels every planned entry
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { NudgeReviewSheet } from './NudgeReviewSheet';
import type { useUpdateNudgePlan } from '../../hooks/mutations/useUpdateNudgePlan';
import type { useSendNudgeNow } from '../../hooks/mutations/useSendNudgeNow';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import {
  addBusinessDaysUtc,
  daysUntilUtc,
  formatUtcIsoLocal,
  localInputToUtcIso,
  relativeDaysTextUtc,
  utcIsoToLocalInput,
  NUDGE_CANCEL_LABEL,
} from '../../lib/dates';
import type { NudgePlanEntry, NudgeSummary } from '../../api/types';

const STEP_TITLE: Record<string, { title: string; sub: string }> = {
  invite: {
    title: 'Chasing the invitation reply',
    sub: 'Auto-follow-ups on the invite thread. They cancel themselves the moment the speaker replies.',
  },
  confirmation: {
    title: 'Chasing outstanding assets',
    sub: "Auto-follow-ups on the confirmation thread. They cancel themselves the moment nothing's outstanding.",
  },
};

type RowState = 'sent' | 'next' | 'planned' | 'overdue' | 'failed' | 'cancelled';

/** Dot + accent treatment per timeline row state (design .nu-row.*). */
const ROW_META: Record<RowState, { dot: string; when: string }> = {
  sent: { dot: 'bg-completed border-completed text-white', when: 'text-completed' },
  next: {
    dot: 'bg-type-speaker border-type-speaker text-white shadow-[0_0_0_4px_var(--color-type-speaker-soft)]',
    when: 'text-type-speaker',
  },
  planned: { dot: 'bg-bg border-border-strong text-text-subtle', when: 'text-text-muted' },
  overdue: { dot: 'bg-waiting border-waiting text-white', when: 'text-waiting' },
  failed: { dot: 'bg-failed border-failed text-white', when: 'text-failed' },
  cancelled: {
    dot: 'border-dashed bg-bg border-border opacity-60 text-text-subtle',
    when: 'text-text-subtle',
  },
};

const ROW_ICON: Partial<Record<RowState, React.ComponentProps<typeof Icon>['name']>> = {
  sent: 'check',
  next: 'send',
  overdue: 'alert',
  failed: 'alert',
  cancelled: 'x',
};

function rowState(entry: NudgePlanEntry, isNext: boolean): RowState {
  if (entry.status === 'sent') return 'sent';
  if (entry.status === 'cancelled') return 'cancelled';
  if (entry.error) return 'failed';
  if (daysUntilUtc(entry.dueAt) < 0) return 'overdue';
  return isNext ? 'next' : 'planned';
}

/** Renders one email's nudge plan panel + its review sheet. */
export function NudgeTimeline({
  summary,
  recipientName,
  recipientEmail,
  fromEmail,
  chaseOver = false,
  update,
  sendNow,
}: {
  summary: NudgeSummary;
  recipientName: string;
  recipientEmail: string | null;
  /** The mailbox the original email went out from (the sheet's From row). */
  fromEmail?: string | null;
  /** The paired receipt step is done — the wait this plan chases already
   *  ended. Neutral "chase closed" pill; the add/stop footer is hidden. */
  chaseOver?: boolean;
  update: ReturnType<typeof useUpdateNudgePlan>;
  sendNow: ReturnType<typeof useSendNudgeNow>;
}) {
  const [sheetIndex, setSheetIndex] = useState<number | null>(null);
  const [rescheduleIdx, setRescheduleIdx] = useState<number | null>(null);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [confirmStopAll, setConfirmStopAll] = useState(false);

  const plan = summary.plan;
  const planned = plan.filter((e) => e.status === 'planned');
  const cancelled = plan.filter((e) => e.status === 'cancelled');
  const busy = update.isPending || sendNow.isPending;
  const heading = STEP_TITLE[summary.stepId] ?? {
    title: `Follow-ups · ${summary.stepId}`,
    sub: 'Same-thread bumps on this sent email.',
  };
  // The NEXT bump = the soonest still-planned entry.
  const nextIdx = plan.reduce<number>(
    (best, e, i) =>
      e.status === 'planned' && (best === -1 || e.dueAt < (plan[best]?.dueAt ?? '')) ? i : best,
    -1,
  );

  // Every edit is a full-plan replace — the server swaps the list wholesale
  // (sent entries must ride along unchanged; it 409s if they don't).
  const commit = (nudges: NudgePlanEntry[], undoMessage?: string) => {
    const prior = plan;
    update.mutate(
      { trackedEmailId: summary.trackedEmailId, nudges },
      undoMessage
        ? {
            onSuccess: () => {
              toast(undoMessage, {
                action: {
                  label: 'Undo',
                  onClick: () =>
                    update.mutate({ trackedEmailId: summary.trackedEmailId, nudges: prior }),
                },
              });
            },
          }
        : undefined,
    );
  };

  const applyReschedule = (idx: number) => {
    if (!rescheduleValue) return;
    // The datetime-local input is the viewer's LOCAL wall clock — convert to
    // the naive-UTC wire format before it goes anywhere near the server.
    const dueAtUtc = localInputToUtcIso(rescheduleValue);
    commit(
      plan.map((e, i) => (i === idx ? { ...e, dueAt: dueAtUtc } : e)),
      `Bump ${idx + 1} moved to ${formatUtcIsoLocal(dueAtUtc)}`,
    );
    setRescheduleIdx(null);
  };

  const cancelEntry = (idx: number) =>
    commit(
      plan.map((e, i) =>
        i === idx && e.status === 'planned'
          ? { ...e, status: 'cancelled' as const, cancelReason: 'manual' }
          : e,
      ),
      `Bump ${idx + 1} skipped`,
    );

  const stopAll = () => {
    commit(
      plan.map((e) =>
        e.status === 'planned' ? { ...e, status: 'cancelled' as const, cancelReason: 'manual' } : e,
      ),
      'All follow-ups stopped',
    );
    setConfirmStopAll(false);
  };

  const addEntry = () => {
    // +5 business days after the last planned entry (or after now when none).
    const last = planned[planned.length - 1];
    const base = last ? last.dueAt : new Date().toISOString().slice(0, 19);
    commit([
      ...plan,
      {
        dueAt: addBusinessDaysUtc(base, 5),
        to: null,
        cc: null,
        status: 'planned',
        sentAt: null,
        error: null,
        cancelReason: null,
        cancelDetail: null,
      },
    ]);
  };

  // TODAY marker goes before the first row that's still in the future.
  const visible = plan
    .map((entry, idx) => ({ entry, idx }))
    .filter(({ entry }) => entry.status !== 'cancelled' || showCancelled);
  const firstFutureVisible = visible.findIndex(
    ({ entry }) => entry.status === 'planned' && daysUntilUtc(entry.dueAt) >= 0,
  );
  // "TODAY · Mon Jul 14" (design D1) — anchors the past/future split.
  const now = new Date();
  const todayText = `${now.toLocaleDateString('en-US', { weekday: 'short' })} ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  // One shared reason across the collapsed rows reads on the toggle itself
  // ("Cancelled earlier (1) — reply received"); mixed reasons stay inside.
  const cancelledReasons = [...new Set(cancelled.map((e) => e.cancelReason).filter(Boolean))];
  const cancelledSuffix =
    cancelledReasons.length === 1 && cancelledReasons[0] != null
      ? ` — ${NUDGE_CANCEL_LABEL[cancelledReasons[0]] ?? cancelledReasons[0]}`
      : '';

  const statusPill =
    summary.status === 'CONFIRMED' || summary.status === 'IN_CONVERSATION' ? (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-completed-border bg-completed-soft px-2 py-0.5 text-[11px] font-medium text-completed">
        <span className="h-1.5 w-1.5 rounded-full bg-completed" /> replied
      </span>
    ) : chaseOver ? (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-soft px-2 py-0.5 text-[11px] font-medium text-text-subtle">
        <span className="h-1.5 w-1.5 rounded-full bg-text-faint" /> chase closed
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-waiting-border bg-waiting-soft px-2 py-0.5 text-[11px] font-medium text-waiting">
        <span className="h-1.5 w-1.5 rounded-full bg-waiting" /> awaiting reply
      </span>
    );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-bg">
      {/* Panel head */}
      <div className="flex items-center gap-3 border-b border-border px-[18px] py-3.5">
        <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg bg-type-speaker-soft text-type-speaker">
          <Icon name="send" size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-type-speaker">
            Follow-up plan
          </div>
          <div className="mt-px text-sm font-bold">{heading.title}</div>
          <div className="mt-px text-xs text-text-muted">{heading.sub}</div>
        </div>
        {statusPill}
      </div>

      {/* Which thread these bumps land on */}
      <div className="flex items-center gap-2 border-b border-border bg-bg-soft px-[18px] py-[9px] text-xs text-text-muted">
        <Icon name="mail" size={13} className="shrink-0" />
        Thread: <span className="truncate font-semibold text-text">{summary.subject}</span>
        {recipientEmail && (
          <span className="ml-auto whitespace-nowrap text-text-faint">to {recipientEmail}</span>
        )}
      </div>

      {/* Timeline */}
      <div className="px-[18px] pb-4 pt-2">
        {plan.length === 0 && (
          <div className="py-3 text-xs text-text-faint">No follow-ups planned for this email.</div>
        )}
        {visible.map(({ entry, idx }, vi) => {
          const state = rowState(entry, idx === nextIdx);
          const meta = ROW_META[state];
          const icon = ROW_ICON[state];
          const isLast = vi === visible.length - 1;
          const whenText =
            entry.status === 'sent'
              ? 'Sent'
              : entry.status === 'cancelled'
                ? 'Cancelled'
                : relativeDaysTextUtc(entry.dueAt);
          const dateText = formatUtcIsoLocal(
            entry.status === 'sent' && entry.sentAt ? entry.sentAt : entry.dueAt,
          );
          return (
            <div key={idx}>
              {vi === firstFutureVisible && (
                <div className="flex items-center gap-2.5 pb-3 pt-1.5">
                  <span className="mono rounded-full border border-type-speaker/25 bg-type-speaker-soft px-2.5 py-0.5 text-[10px] font-bold tracking-[0.04em] text-type-speaker">
                    TODAY · {todayText}
                  </span>
                  <span className="h-px flex-1 bg-type-speaker/25" />
                </div>
              )}
              <div className="relative grid grid-cols-[30px_1fr_auto] items-start gap-3 pb-3.5">
                {!isLast && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute bottom-[-4px] left-[14px] top-[26px] w-0.5',
                      state === 'sent' ? 'bg-completed/40' : 'bg-border',
                    )}
                  />
                )}
                <span className="flex justify-center pt-[3px]">
                  <span
                    className={cn(
                      'relative z-10 grid h-[22px] w-[22px] place-items-center rounded-full border-2',
                      meta.dot,
                    )}
                  >
                    {icon && <Icon name={icon} size={11} />}
                  </span>
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-[13px] font-semibold">Bump {idx + 1}</span>
                    <span className={cn('text-xs font-semibold', meta.when)}>{whenText}</span>
                    <span className="tabular text-xs text-text-subtle">· {dateText}</span>
                  </div>
                  {entry.status === 'sent' && entry.sentSummary && (
                    <div className="mt-[3px] text-[11.5px] italic leading-[1.45] text-text-subtle">
                      “{entry.sentSummary}”
                    </div>
                  )}
                  {state === 'next' && (
                    <div className="mt-[3px] text-[11.5px] leading-[1.45] text-text-subtle">
                      Next to go out. Recomputed at send time —{' '}
                      {summary.stepId === 'invite'
                        ? 'auto-cancelled if the speaker replies first.'
                        : 'it only chases what’s still outstanding then.'}
                    </div>
                  )}
                  {state === 'planned' && (
                    <div className="mt-[3px] text-[11.5px] leading-[1.45] text-text-subtle">
                      {summary.stepId === 'invite'
                        ? 'Only sends if the speaker still hasn’t replied.'
                        : 'Only sends if something’s still outstanding then.'}
                    </div>
                  )}
                  {entry.to && (
                    <div className="mt-[3px] text-[11.5px] text-text-subtle">
                      to {entry.to.join(', ')}
                    </div>
                  )}
                  {entry.status === 'cancelled' && entry.cancelReason && (
                    <div
                      className={cn(
                        'mt-[3px] text-[11.5px]',
                        entry.cancelReason === 'inbound_activity'
                          ? 'font-medium text-waiting'
                          : 'text-text-subtle',
                      )}
                    >
                      {NUDGE_CANCEL_LABEL[entry.cancelReason] ?? entry.cancelReason}
                      {entry.cancelReason === 'inbound_activity' && entry.cancelDetail?.from && (
                        <>
                          {' — '}
                          {entry.cancelDetail.from} wrote
                          {entry.cancelDetail.at
                            ? ` ${formatUtcIsoLocal(entry.cancelDetail.at)}`
                            : ''}
                          {entry.cancelDetail.inThread ? ' on this thread' : ''}
                        </>
                      )}
                    </div>
                  )}
                  {entry.status === 'planned' && entry.error && (
                    <div className="mono mt-[5px] rounded-md border border-failed-border bg-failed-soft px-2 py-1.5 text-[11.5px] text-failed">
                      Send failed · retrying — {entry.error}
                    </div>
                  )}
                  {rescheduleIdx === idx && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        value={rescheduleValue}
                        onChange={(e) => setRescheduleValue(e.target.value)}
                        disabled={busy}
                        aria-label={`Bump ${idx + 1} new date`}
                        className="rounded-md border border-border bg-bg px-1.5 py-1 text-xs text-text disabled:opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => applyReschedule(idx)}
                        disabled={busy || !rescheduleValue}
                        className="inline-flex items-center gap-1 rounded-md border border-border-strong px-2 py-1 text-[11px] font-medium transition-colors hover:bg-bg-hover disabled:opacity-50"
                      >
                        <Icon name="check" size={11} /> Move
                      </button>
                      <button
                        type="button"
                        onClick={() => setRescheduleIdx(null)}
                        className="rounded-md border border-border px-2 py-1 text-[11px] font-medium text-text-muted transition-colors hover:bg-bg-hover"
                      >
                        Keep
                      </button>
                    </div>
                  )}
                </div>
                {/* Row actions */}
                <div className="flex items-center gap-1.5 pt-0.5">
                  {entry.status === 'sent' ? (
                    <span className="inline-flex items-center gap-[5px] text-[11px] text-text-faint">
                      <Icon name="check" size={11} /> Sent · locked
                    </span>
                  ) : entry.status === 'planned' ? (
                    <>
                      {idx === nextIdx && (
                        <button
                          type="button"
                          onClick={() => setSheetIndex(idx)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-[11px] font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
                        >
                          <Icon name="send" size={11} /> Send now
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setSheetIndex(idx)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded-md border border-border-strong px-2 py-1 text-[11px] font-medium transition-colors hover:bg-bg-hover disabled:opacity-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        title="Reschedule"
                        aria-label={`Reschedule bump ${idx + 1}`}
                        onClick={() => {
                          setRescheduleIdx(rescheduleIdx === idx ? null : idx);
                          // Prefill the picker with the LOCAL wall clock.
                          setRescheduleValue(utcIsoToLocalInput(entry.dueAt));
                        }}
                        disabled={busy}
                        className="grid h-[26px] w-[26px] place-items-center rounded-md border border-border text-text-subtle transition-colors hover:bg-bg-hover disabled:opacity-50"
                      >
                        <Icon name="time" size={13} />
                      </button>
                      <button
                        type="button"
                        title="Skip this bump"
                        aria-label={`Skip bump ${idx + 1}`}
                        onClick={() => cancelEntry(idx)}
                        disabled={busy}
                        className="grid h-[26px] w-[26px] place-items-center rounded-md border border-border text-text-subtle transition-colors hover:bg-bg-hover disabled:opacity-50"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}

        {cancelled.length > 0 && (
          <button
            type="button"
            onClick={() => setShowCancelled((v) => !v)}
            className="flex items-center gap-[7px] py-1 text-[11.5px] font-semibold text-text-subtle"
          >
            <Icon
              name="chevron-right"
              size={13}
              className={cn('text-text-faint transition-transform', showCancelled && 'rotate-90')}
            />
            Cancelled earlier ({cancelled.length})
            {cancelledSuffix && (
              <span className="font-medium text-text-faint">{cancelledSuffix}</span>
            )}
          </button>
        )}
      </div>

      {/* Panel foot — hidden once the chase is over (adding bumps to a
          closed chase is a footgun; the server would auto-cancel them). */}
      {!chaseOver && (
        <div className="flex items-center gap-2 border-t border-border bg-bg-soft px-[18px] py-3">
          <button
            type="button"
            onClick={addEntry}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-text-muted transition-colors hover:bg-bg-hover disabled:opacity-50"
          >
            <Icon name="plus" size={11} /> Add bump{' '}
            <span className="text-text-faint">+5 business days</span>
          </button>
          <span className="flex-1" />
          {planned.length > 0 &&
            (confirmStopAll ? (
              <span className="flex items-center gap-2 text-[11px]">
                <span className="text-text-muted">
                  Stop all {planned.length} planned follow-up{planned.length === 1 ? '' : 's'}?
                </span>
                <button
                  type="button"
                  onClick={stopAll}
                  disabled={busy}
                  className="rounded-md border border-failed-border px-2 py-1 font-medium text-failed transition-colors hover:bg-failed-soft disabled:opacity-50"
                >
                  Stop all
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmStopAll(false)}
                  className="rounded-md border border-border px-2 py-1 font-medium text-text-muted transition-colors hover:bg-bg-hover"
                >
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmStopAll(true)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-failed-border px-2 py-1 text-[11px] font-medium text-failed transition-colors hover:bg-failed-soft disabled:opacity-50"
              >
                <Icon name="x" size={12} /> Stop all follow-ups
              </button>
            ))}
        </div>
      )}

      {sheetIndex != null && (
        <NudgeReviewSheet
          open
          onOpenChange={(open) => {
            if (!open) setSheetIndex(null);
          }}
          trackedEmailId={summary.trackedEmailId}
          index={sheetIndex}
          recipientName={recipientName}
          fromEmail={fromEmail}
          stepId={summary.stepId}
          sendNow={sendNow}
        />
      )}
    </div>
  );
}
